import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug } from '../lib/api';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('om-os');
        setPageData(page);
      } catch (err) {
        console.error('Error loading about data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Update document metadata
  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || 'Om os - Røjsmose Isolering ApS';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageData.meta_description || '');
      } else {
        const newMeta = document.createElement('meta');
        newMeta.name = 'description';
        newMeta.content = pageData.meta_description || '';
        document.head.appendChild(newMeta);
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  const mainImage = pageData?.images?.[0] || 'https://assets.auxodev.dk/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/mf-sky1.jpeg';

  return (
    <div className="py-16 sm:py-24 bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Page Header */}
        <div className="border-b border-slate-200 pb-8 mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl mt-2">
            {pageData?.title || 'Om os'}
          </h1>
        </div>

        {/* Content Area */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          {/* Left Column: Text Content */}
          <div className="md:col-span-7 space-y-8">
            <div
              className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-6 text-lg"
              dangerouslySetInnerHTML={{ __html: pageData?.content || '' }}
            />
          </div>

          {/* Right Column: Original Image & Highlights */}
          <div className="md:col-span-5 space-y-6">
            <div className="overflow-hidden rounded-none border border-slate-200 bg-slate-50 shadow-sm">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${mainImage.replace('https://assets.auxodev.dk/', '')}`}
                alt="Røjsmose Isolering"
                className="w-full h-auto object-cover rounded-none"
              />
            </div>

            {/* Key Highlights Card */}
            <div className="bg-slate-50 border border-slate-200 p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-brand-orange text-white">
                  <i className="fa-solid fa-award text-2xl" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-brand-navy">40 års erfaring</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Fire årtiers stolt fagmæssig ekspertise i isoleringsbranchen.
                  </p>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-brand-navy text-white">
                  <i className="fa-solid fa-people-roof text-2xl" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-brand-navy">Stolt familievirksomhed</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Bygget på nære relationer, troværdighed og personligt engagement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact CTA Section */}
        <div className="mt-20 rounded-none bg-brand-navy p-8 sm:p-12 lg:p-16 text-white relative overflow-hidden shadow-xl">
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#f97316_1px,transparent_1px)] [background-size:16px_16px]" />
          
          <div className="relative z-10 max-w-3xl">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl mt-2">
              Skal vi hjælpe dig med dit næste isoleringsprojekt?
            </h2>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed">
              Vi står altid klar med professionel rådgivning og et uforpligtende tilbud tilpasset dine behov. Kontakt os i dag for en uforpligtende snak.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-full bg-brand-orange px-6 py-3 text-base font-bold text-white shadow-sm transition-all duration-200 hover:bg-brand-orange-hover hover:scale-[1.02] active:scale-[0.98]"
              >
                Kontakt os i dag
              </Link>
              <a
                href="tel:42585848"
                className="inline-flex items-center justify-center rounded-full border-2 border-white bg-transparent px-6 py-3 text-base font-bold text-white transition-all duration-200 hover:bg-transparent hover:border-white/80"
              >
                <i className="fa-solid fa-phone mr-2" aria-hidden="true" />
                Ring 42 58 58 48
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
